import { Backdrop, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormGroup, Grid, IconButton, List, ListItem, ListItemText, Paper, Typography } from '@mui/material';
import React, { useState, useEffect, Fragment } from 'react';
import SynamediaClientsApi from "../utils/SynamediaClientsApi"
import { FIELD_TYPES, dateAsString, getDateText, getInternationalPhone } from '../utils/utils';
import JSData from './JSData';
import SettingsIcon from '@mui/icons-material/Settings';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';
import ObjectsList from './ObjectsList';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import makeStyles from '@mui/styles/makeStyles';
import { Fields } from './Fields';

const useStyles = makeStyles((theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },
}))

const STATUS_COLORS = {
    'Found a Job': '#34a853',
    'Freeze': '#6d9eeb',
    'Finished': '#5d6f8c',
    'Interviewing': '#ed6804',
    'Waiting to start': '#ffcc00'
}

const EditRowDialog = (props) => {
    const objectToEdit = props.objectToEdit
    const isLoading = props.isLoading

    const [newData, setNewData] = useState({});

    useEffect(() => {
        setNewData({})
    }, [objectToEdit]);

    const handleClose = () => {
        setNewData({})
        props.setObjectToEdit()
    }

    const getValue = (name, defaultValue) => {
        if (newData[name] !== undefined){
            return newData[name]
        } else if (objectToEdit && objectToEdit[name] !== undefined){
            return objectToEdit[name]
        } else {
            return defaultValue
        }
    }

    const handleChangeField = e => {
        setNewData({...newData,[e.target.name]: e.target.value})
    };

    const handleSave = e => {
        e.preventDefault();

        props.handleUpdateClientData(objectToEdit._id, newData)
    }

    const fields = [
        { name: 'email', label: 'Email', defaultValue: '', type:FIELD_TYPES.TEXT, sm:12},  
        { name: 'fullName', label: 'Name', defaultValue: '', type:FIELD_TYPES.TEXT, sm:12},  
        { name: 'originalTitle', label: 'Original Title', defaultValue: '', type:FIELD_TYPES.TEXT, sm:12},  
        { name: 'update', label: 'Update', defaultValue: '', type:FIELD_TYPES.MULTILINE, sm:12},  
    ]

    return (
        <Dialog onClose={handleClose} open={(objectToEdit!==undefined)} fullWidth maxWidth="md">
            <form onSubmit={handleSave}>
                <DialogContent dividers>
                    <Fields fields={fields} getValue={getValue} handleChange={handleChangeField}/>
                </DialogContent>
                <DialogActions>
                    <Button disabled={isLoading} onClick={handleClose} color="primary" size="small" aria-label="Cancel">
                        Cancel
                    </Button>
                    <Button
                        disabled={isLoading}
                        type="submit"
                        variant="outlined"
                        color="primary"
                        size="small"
                        aria-label='Save'
                    >
                        Save
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}

const CustomPieTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length && payload[0]) {
        const item = payload[0]

        return (
            <Paper elevation={3} style={{padding:8, margin:8}}> 
                <Typography><strong>{item.name}:</strong> {item.value}</Typography>
            </Paper>
        );
    }
  
    return null;
};

function SearchExtra(props) {
    const extraSearch = props.extraSearch || {}
    const showExtraSearch = props.showExtraSearch || false
    const extraSearchField = props.extraSearchField
    const statuses = props.statuses || []

    const [newData, setNewData] = useState({...extraSearch});

    useEffect(() => {
        if(showExtraSearch){
            setNewData({...extraSearch})
        }
    }, [showExtraSearch, extraSearch]);

    const handleCancel = () => {
        setNewData({...extraSearch})
        props.setShowExtraSearch(false)
    }

    const handleToggleStatus = e => {
        let newStatuses = []

        if (e.target.checked){
            newStatuses = [...newData.status, e.target.name]
        } else {
            newStatuses = newData.status.filter(statusObj=>(statusObj!==e.target.name))
        }

        setNewData({...newData, status:newStatuses})
    }

    const selectAllStatuses = () => {
        setNewData({...newData, status:[...statuses]})
    }

    const clearStatuses = () => {
        setNewData({...newData, status:[]})
    }

    const clearDates = () => {
        setNewData({...newData, from:undefined, to: undefined})
    }

    const handleSort = sort => {
        props.handleSort(sort)
    }

    const handleSave = e => {
        e.preventDefault();

        props.handleFilter(newData)
    }

    const getValue = (name, defaultValue) => {
        if (newData[name] !== undefined){
            return newData[name]
        } else {
            return defaultValue
        }
    }

    const handleChangeField = e => {
        setNewData({...newData,[e.target.name]: e.target.value})
    };

    const fields = [
        { name: 'from', label:'From', defaultValue: '', type:FIELD_TYPES.DATE2, sm:6},
        { name: 'to', label:'To', defaultValue: '', type:FIELD_TYPES.DATE2, sm:6},
    ]

    return (
        <Dialog onClose={handleCancel} open={showExtraSearch} fullWidth maxWidth="xs">
            <form onSubmit={handleSave}>
                <DialogContent dividers>
                    <List component="nav" dense>
                        <ListItem button onClick={()=>handleSort(1)} style={{paddingLeft: 0}}>
                            <ListItemText primary="Sort A to Z" />
                        </ListItem>
                        <ListItem button onClick={()=>handleSort(-1)} style={{paddingLeft: 0}}>
                            <ListItemText primary="Sort Z to A" />
                        </ListItem>
                    </List>
                    {(extraSearchField==='date')&&<>
                        <Divider/>
                        <div>
                            <Button onClick={clearDates} color="primary" style={{textTransform: 'lowercase', paddingLeft: 0, justifyContent: 'flex-start'}}>Clear</Button>
                        </div>
                        <Fields fields={fields} getValue={getValue} handleChange={handleChangeField}/>
                    </>}
                    {(extraSearchField==='status')&&<>
                        <Divider/>
                        <div>
                            <Button onClick={selectAllStatuses} color="primary" style={{textTransform: 'lowercase', paddingLeft: 0}}>Select All</Button>
                            <Button onClick={clearStatuses} color="primary" style={{textTransform: 'lowercase'}}>Clear</Button>
                        </div>
                        <FormControl variant="standard" component="fieldset">
                            <FormGroup>
                                {statuses.map(status => (
                                    <FormControlLabel key={status}
                                        control={<Checkbox color='primary' checked={(newData.status||[]).includes(status)} onChange={handleToggleStatus} name={status} />}
                                        label={status}
                                    />
                                ))}
                            </FormGroup>
                        </FormControl>
                    </>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} color="primary" size="small" aria-label="Close">
                        Close
                    </Button>
                    {<Button
                        type="submit"
                        variant="outlined"
                        color="primary"
                        size="small"
                        aria-label='Filter'
                    >
                        Filter
                    </Button>}
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default function SynamediaClients(props) {
    const permissions = props.permissions || {}
    const company = props.company
    const showMessageDialog = props.showMessageDialog

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const [unapprovedClients, setUnapprovedClients] = useState([]);
    const [approvedClients, setApprovedClients] = useState([]);
    const [filteredApprovedClients, setFilteredApprovedClients] = useState([]);
    const [synamediaEmails, setSynamediaEmails] = useState([]);

    const [cells, setCells] = useState([]);
    const [chartStatusesData, setChartStatusesData] = useState([]);
    const [quotas, setQuotas] = useState([]);
    const [quotasTotal, setQuotasTotal] = useState();
    const [statuses, setStatuses] = useState([]);

    const [showExtraSearch, setShowExtraSearch] = useState(false);
    const [extraSearch, setExtraSearch] = useState({});
    const [extraSearchField, setExtraSearchField] = useState();

    const [showSettings, setShowSettings] = useState(false);

    const [objectToEdit, setObjectToEdit] = useState();
    
    const isJh = permissions.Admin || permissions.JHAdmin

    useEffect(() => {
        setIsLoading(true)

        SynamediaClientsApi.getSynamediaClients({company},(response)=>{
            updateDataByRespone(response)
            setExtraSearch({status:response.statuses})

            setIsLoading(false)

            if (response.newFoundJob){
                showMessageDialog('Found a job', response.newFoundJob)
            }
        }, (error) => {
            if (error && error.response && error.response.data){
                setError(error.response.data)
            } else {
                setError('Something went wrong')
            }
            setIsLoading(false)
        })
    }, [company, permissions, showMessageDialog]);

    const handleApproveClient = clientId => () => {
        setIsLoading(true)

        SynamediaClientsApi.approveSynamediaClient({clientId, company},(response)=>{
            updateDataByRespone(response)

            setIsLoading(false)
        }, (error) => {
            if (error && error.response && error.response.data){
                setError(error.response.data)
            } else {
                setError('Something went wrong')
            }
            setIsLoading(false)
        })
    }

    const updateDataByRespone = response => {
        setUnapprovedClients(response.unapprovedClients || [])
        setApprovedClients(response.approvedClients || [])
        setFilteredApprovedClients(response.approvedClients || [])
        setCells(response.cells || [])
        setChartStatusesData(response.chartStatusesData || [])
        setQuotas(response.quotas || [])
        setQuotasTotal(response.quotasTotal || 0)
        setStatuses(response.statuses || [])
        
        if (response.synamediaEmails){
            setSynamediaEmails(response.synamediaEmails)
        } else {
            setSynamediaEmails([])
        }
    }

    const handleChangeEmails = newEmails => {
        setIsLoading(true)

        SynamediaClientsApi.updateSynamediaEmailsList({synamediaEmails:newEmails, company},(response)=>{
            setSynamediaEmails(response)
            setIsLoading(false)
        }, (error) => {
            if (error && error.response && error.response.data){
                setError(error.response.data)
            } else {
                setError('Something went wrong')
            }
            setIsLoading(false)
        })
    }

    const handleCloseSettings = () => {
        setShowSettings(false)
    }

    const handleEditRow=(row, params={})=>{
        if(isJh){
            setObjectToEdit({...row})
        }
    }

    const handleUpdateClientData = (id, newData) => {
        setIsLoading(true)

        SynamediaClientsApi.updateSynamediaClientData({id, newData, company},(response)=>{
            updateDataByRespone(response)
            setObjectToEdit()
            setIsLoading(false)
        }, (error) => {
            if (error && error.response && error.response.data){
                setError(error.response.data)
            } else {
                setError('Something went wrong')
            }
            setIsLoading(false)
        })
    }

    const handleShowFilterColumn=(field)=>{
        setExtraSearchField(field)
        setShowExtraSearch(true)
    }

    const handleFilter = newSearch => {
        const newFilteredApprovedClients = approvedClients.filter(row=>{
            let toShow = newSearch.status.includes(row.status)

            if (toShow && newSearch.from){
                toShow = (row.approveDate >= newSearch.from)
            }

            if (toShow && newSearch.to){
                toShow = (row.approveDate <= newSearch.to)
            }

            return toShow
        })

        setFilteredApprovedClients(newFilteredApprovedClients)
        setExtraSearch({...newSearch})
        setShowExtraSearch(false)
    }

    const handleSort = sort => {
        const newFilteredApprovedClients = filteredApprovedClients.sort((a,b) => {
            if (a[extraSearchField]>b[extraSearchField]){
                return sort
            } else {
                return -1 * sort
            }
        })

        setFilteredApprovedClients(newFilteredApprovedClients)
        setShowExtraSearch(false)
    }

    const lastQuota = (quotas[quotas.length - 1]||{}).count || 0

    const classes = useStyles();

    return (
        <div className="fullWidthContainer" >
            <Backdrop className={classes.backdrop} open={isLoading || false}>
                <CircularProgress />
            </Backdrop>
            <SearchExtra 
                extraSearch={extraSearch} 
                showExtraSearch={showExtraSearch} 
                setShowExtraSearch={setShowExtraSearch} 
                handleFilter={handleFilter}
                handleSort={handleSort}
                extraSearchField={extraSearchField}
                statuses={statuses}
            />
            <EditRowDialog objectToEdit={objectToEdit} setObjectToEdit={setObjectToEdit} isLoading={isLoading} handleUpdateClientData={handleUpdateClientData}/>
            <Grid container spacing={1} style={{height: 278}}>
                <Grid item sm={4} xs={12}>
                    {(!isLoading)&&<div style={{margin:'2px 4px', padding:32}}>
                        {(error)&&<Typography color="error">{error}</Typography>}
                        <Typography variant="h6">Waiting for approval: {unapprovedClients.length}</Typography>
                        <List dense style={{overflow: 'auto',height: 190}}>
                            {unapprovedClients.map((client, index) => {
                                const internationalPhone = getInternationalPhone(client.phone)
                                const waLink = `https://wa.me/`+internationalPhone
                                return (
                                    <Fragment key={client._id}>
                                        <div style={{padding: '4px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                            <div style={{margin: '6px 0', marginRight: 16}}>
                                                <Typography variant='body2'>{client.fullName} - <a href={`mailto:${client.email}`} target='_blank' rel="noopener noreferrer" style={{color: 'inherit'}}>{client.email}</a></Typography>
                                                <Typography variant='body2' color="textSecondary">{getDateText(client.datecreated)} - {internationalPhone?<a href={waLink} target='_blank' rel="noopener noreferrer" style={{color: 'inherit'}}>{client.phone}</a>:<>{client.phone}</>}</Typography>
                                            </div>
                                            <div>
                                                <Button size='small' disabled={isLoading} color='primary' variant='contained' style={{textTransform: 'capitalize'}}
                                                    onClick={handleApproveClient(client._id)}>Approve</Button>
                                            </div>
                                        </div>
                                        <Divider />
                                    </Fragment>
                                )
                            })}
                        </List>
                    </div>}
                </Grid>
                <Grid item sm={4} xs={12}>
                    {(chartStatusesData.length > 0)&&<div style={{margin:'2px 4px', padding:32, display: 'flex', alignItems: 'center'}}>
                        <div>
                            {chartStatusesData.map((entry, index) => (
                                <div key={`cell-${index}`} style={{display: 'flex', alignItems: 'center', marginBottom: 8}}>
                                    <i style={{backgroundColor:STATUS_COLORS[entry.name], width: 15,display: 'inline-block',height: 10,marginRight: 4}}></i> {entry.name}: {entry.value}
                                </div>
                            ))}
                            <div style={{display: 'flex', alignItems: 'center', minWidth: 190, marginBottom: 8}}>
                                <i style={{width: 15,display: 'inline-block',height: 10,marginRight: 4}}></i> <strong style={{fontSize: 18}}>Total</strong>: {approvedClients.length}
                            </div>
                        </div>
                        <div>
                            <PieChart width={200} height={200}>
                                <Pie
                                    data={chartStatusesData}
                                    cx="50%"
                                    cy="50%"
                                    label
                                    outerRadius={70}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {chartStatusesData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name]}/>
                                    ))}
                                    
                                </Pie>
                                <Tooltip content={<CustomPieTooltip />} />
                            </PieChart>
                        </div>
                    </div>} 
                </Grid>
                <Grid item sm={4} xs={12}>
                    {(!isLoading)&&<div style={{alignItems: 'center', height: '100%', display: 'flex'}}>
                        <div>
                            <Typography variant="h6">Prepaid Packages</Typography>  
                            <Typography><strong>{lastQuota - (quotasTotal - approvedClients.length)} / {lastQuota}</strong></Typography>
                            <IconButton
                                color='primary'
                                aria-label="WhatsApp"
                                href={"https://wa.me/message/" + "Q2YZIIT22QUGO1"}
                                target='_blank'
                                rel="noopener noreferrer"
                                size="large">
                                <WhatsAppIcon/>
                            </IconButton>
                            {(isJh)&&<IconButton
                                color='primary'
                                onClick={()=>setShowSettings(true)}
                                aria-label="Settings"
                                disabled={(isLoading)}
                                size="large">
                                <SettingsIcon/>
                            </IconButton>}
                        </div>
                    </div>}
                </Grid>
            </Grid>
            {(isJh)&&<Dialog onClose={handleCloseSettings} open={showSettings} fullWidth maxWidth="md">
                <DialogTitle>Settings</DialogTitle>
                <DialogContent dividers>
                    <Typography variant='h6'>Emails for notifications</Typography>
                    <ObjectsList ObjectsList={synamediaEmails} setNewObjects={handleChangeEmails} fieldName="email" fieldInputType="email" disabled={isLoading}/>
                    <Typography variant='h6'>Quotas</Typography>
                    <List dense>
                        {quotas.map((quota, index) => {
                            return (
                                <Fragment key={index}>
                                    <ListItem>
                                        <ListItemText primary={quota.count + (quota.date?(' - ' + dateAsString(new Date(quota.date))):'')}/>
                                    </ListItem>
                                    <Divider />
                                </Fragment>
                            )
                        })}
                    </List>
                </DialogContent>
                <DialogActions>
                    {<Button disabled={isLoading} onClick={handleCloseSettings} color="primary" size="small" aria-label="Close">
                        Close
                    </Button>}
                </DialogActions>
            </Dialog>}
            {(!isLoading)&&<Paper style={{margin:'2px 4px', position:'relative', overflow:'auto', height:'calc(100% - 272px)'}} elevation={1}>
                <JSData cells={cells} data={filteredApprovedClients} handleEditRow={handleEditRow} handleShowFilterColumn={handleShowFilterColumn} rowBackgroundColor='rowBackgroundColor'/>
            </Paper>}
        </div>
    );
}